/**
 * B15 (Jurisdiction) 공통코드 → CODE_NAME 표시용
 * Notice Corporate — controller_jurisdiction_statute 필드는 코드(B15237)가 아닌 코드명을 저장.
 */

import { baseCodeApi } from '@/shared/api/baseCodeApi';

let b15NameMapCache: Map<string, string> | null = null;
let b15LoadPromise: Promise<Map<string, string>> | null = null;

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function isB15Code(value: string): boolean {
  return /^B15\d+/i.test(trim(value));
}

/** Corporate controller notice — global contact lookup (B09001 only) */
export const NOTICE_CORPORATE_CONTACT_CATEGORY_CD = 'B09001';

/** Singapore — Controller UEN 에 CONTACT_REG_NO 바인딩 */
export const SG_CONTROLLER_JURISDICTION_B15 = 'B15201';

export function isSgControllerJurisdiction(code: string): boolean {
  return trim(code).toUpperCase() === SG_CONTROLLER_JURISDICTION_B15;
}

export async function ensureB15JurisdictionNameMap(): Promise<Map<string, string>> {
  if (b15NameMapCache) return b15NameMapCache;
  if (b15LoadPromise) return b15LoadPromise;

  b15LoadPromise = (async () => {
    const map = new Map<string, string>();
    try {
      const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
      const codes = (response as { codes?: unknown[] }).codes
        ?? (response as { data?: { codes?: unknown[] } }).data?.codes
        ?? [];
      for (const code of codes as Record<string, unknown>[]) {
        const totalCode = trim(code.totalCode ?? code.TOTAL_CODE).toUpperCase();
        const codeName = trim(
          code.defaultCodeName ?? code.DEFAULT_CODE_NAME ?? code.codeName ?? code.CODE_NAME,
        );
        if (totalCode && codeName) map.set(totalCode, codeName);
      }
    } catch (e) {
      console.warn('[b15JurisdictionDisplay] B15 load failed:', e);
    }
    b15NameMapCache = map;
    return map;
  })();

  return b15LoadPromise;
}

export function resolveB15CodeToName(code: string, map: Map<string, string>): string {
  const raw = trim(code);
  if (!raw) return '';
  if (!isB15Code(raw)) return raw;
  const key = raw.toUpperCase();
  return map.get(key) || map.get(raw) || raw;
}

/** JURISDICTION(B15) + INCORPORATION_STATUTE(텍스트) → DOCX 표시 문자열 */
export function formatJurisdictionStatuteDisplay(
  jurisdictionRaw: string,
  statuteRaw: string,
  map: Map<string, string>,
): string {
  const jurisdiction = resolveB15CodeToName(jurisdictionRaw, map);
  const statute = trim(statuteRaw);
  if (jurisdiction && statute) return `${jurisdiction} — ${statute}`;
  return jurisdiction || statute;
}

/** 단일 필드에 코드만 있거나 "코드 — statute" 형태일 때 CODE_NAME 으로 정규화 */
export function resolveJurisdictionStatuteField(raw: string, map: Map<string, string>): string {
  const s = trim(raw);
  if (!s) return '';

  const dashParts = s.split(/\s*[—–]\s*/);
  if (dashParts.length >= 2) {
    const j = resolveB15CodeToName(dashParts[0], map);
    const rest = dashParts.slice(1).map((p) => trim(p)).filter(Boolean);
    return [j, ...rest].join(' — ');
  }

  return resolveB15CodeToName(s, map);
}

export function normalizeNoticeCorporateJurisdictionFields(
  model: {
    controllerJurisdictionStatute: string;
  },
  map: Map<string, string>,
): void {
  model.controllerJurisdictionStatute = resolveJurisdictionStatuteField(
    model.controllerJurisdictionStatute,
    map,
  );
}
